import React, { useState, useEffect, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import * as DM from "@/components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Receipt, ShoppingCart, Wallet, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";

/* Project-side menu: Add expense / Add purchase request / Add reimbursement.
   Each form pre-binds project_id so the entry auto-links to this project.
   Reimbursement extends the purchase form with a mandatory Google Drive receipt link. */
const KIND_LABEL = {
  expense: "Add expense",
  purchase: "Add purchase request",
  reimbursement: "Add reimbursement",
};

export default function ProjectAddActions({ project, onCreated }) {
  const [kind, setKind] = useState(null); // 'expense' | 'purchase' | 'reimbursement' | null
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);

  // Lazy-load finance categories the first time the user opens any dialog
  useEffect(() => {
    if (!kind || categories.length) return;
    api.get("/finance/categories")
       .then((r) => setCategories(r.data?.categories || []))
       .catch(() => setCategories([]));
  }, [kind, categories.length]);

  // Default form shape depending on `kind`
  const defaults = useMemo(() => {
    if (kind === "expense") {
      return {
        date: new Date().toISOString().slice(0, 10),
        amount: "", vendor: "", category: "", sub_category: "",
        description: "", payment_mode: "UPI", invoice_link: "",
        project_name: project?.name || "", project_id: project?.id,
      };
    }
    // purchase + reimbursement share the same form
    return {
      item: "", quantity: 1, estimated_cost: "", justification: "",
      vendor: "", category: "", receipt_url: "",
      project_id: project?.id,
      request_type: kind === "reimbursement" ? "reimbursement" : "purchase",
    };
  }, [kind, project]);
  const [form, setForm] = useState(defaults);
  useEffect(() => { setForm(defaults); }, [defaults]);

  const cat = categories.find((c) => c.category === form.category);

  const submit = async () => {
    setSaving(true);
    try {
      if (kind === "expense") {
        if (!form.amount || Number(form.amount) <= 0) throw new Error("Amount must be > 0");
        if (!form.description.trim()) throw new Error("Description required");
        await api.post(`/projects/${project.id}/expenses`, {
          ...form,
          amount: Number(form.amount),
          drive_link: form.invoice_link,
        });
        toast.success("Expense added · linked to this project");
      } else {
        if (!form.item.trim()) throw new Error("Item required");
        if (!form.estimated_cost || Number(form.estimated_cost) <= 0) throw new Error("Estimated cost must be > 0");
        if (kind === "reimbursement" && !form.receipt_url.trim()) {
          throw new Error("Reimbursement requires a Google Drive receipt link");
        }
        await api.post("/purchase-requests", {
          ...form,
          estimated_cost: Number(form.estimated_cost),
          quantity: Number(form.quantity || 1),
        });
        toast.success(`${kind === "reimbursement" ? "Reimbursement" : "Purchase"} request submitted`);
      }
      setKind(null);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <>
      <DM.DropdownMenu>
        <DM.DropdownMenuTrigger asChild>
          <Button variant="outline" className="rounded-sm" data-testid="project-add-actions">
            <Plus size={14} className="mr-1.5" /> Add to project
          </Button>
        </DM.DropdownMenuTrigger>
        <DM.DropdownMenuContent align="end" className="rounded-sm w-56">
          <DM.DropdownMenuItem onClick={() => setKind("expense")} data-testid="project-add-expense" className="text-xs">
            <Receipt size={13} className="mr-2 text-[#F54800]" /> Add expense
          </DM.DropdownMenuItem>
          <DM.DropdownMenuItem onClick={() => setKind("purchase")} data-testid="project-add-purchase" className="text-xs">
            <ShoppingCart size={13} className="mr-2 text-blue-600" /> Add purchase request
          </DM.DropdownMenuItem>
          <DM.DropdownMenuItem onClick={() => setKind("reimbursement")} data-testid="project-add-reimbursement" className="text-xs">
            <Wallet size={13} className="mr-2 text-emerald-600" /> Add reimbursement
          </DM.DropdownMenuItem>
        </DM.DropdownMenuContent>
      </DM.DropdownMenu>

      <Dialog open={!!kind} onOpenChange={(o) => !o && setKind(null)}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="project-add-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{KIND_LABEL[kind] || ""} · {project?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">
            {kind === "expense"
              ? "Auto-linked to this project. Visible to admins in Finance → Expenses."
              : kind === "reimbursement"
                ? "Reimbursements need a mandatory Google Drive receipt link."
                : "Auto-linked to this project. Admin will review in Studio → Purchases."}
          </p>

          {kind === "expense" ? (
            <ExpenseForm form={form} setForm={setForm} categories={categories} cat={cat} />
          ) : (
            <PurchaseForm form={form} setForm={setForm} categories={categories} kind={kind} />
          )}

          <DialogFooter>
            <Button onClick={submit} disabled={saving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="project-add-submit">
              {saving ? "Saving…" : "Submit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ─── small subforms ─── */
function ExpenseForm({ form, setForm, categories, cat }) {
  return (
    <div className="grid grid-cols-2 gap-3 mt-2">
      <Field label="Date"><Input type="date" className="rounded-sm" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
      <Field label="Amount ₹"><Input type="number" className="rounded-sm" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount" /></Field>
      <Field label="Vendor"><Input className="rounded-sm" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} /></Field>
      <Field label="Payment mode">
        <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
          <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
          <SelectContent>{["UPI", "Bank", "Card", "Cash", "Wire"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Category">
        <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v, sub_category: "" })}>
          <SelectTrigger className="rounded-sm" data-testid="expense-category"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>{categories.map((c) => <SelectItem key={c.category} value={c.category}>{c.category}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Sub-category">
        <Select value={form.sub_category} onValueChange={(v) => setForm({ ...form, sub_category: v })} disabled={!cat}>
          <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>{(cat?.sub_categories || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <div className="col-span-2"><Field label="Description"><Input className="rounded-sm" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="expense-desc" /></Field></div>
      <div className="col-span-2"><Field label="Drive / invoice link"><Input className="rounded-sm" placeholder="https://drive.google.com/…" value={form.invoice_link} onChange={(e) => setForm({ ...form, invoice_link: e.target.value })} /></Field></div>
    </div>
  );
}

function PurchaseForm({ form, setForm, categories, kind }) {
  return (
    <div className="grid grid-cols-2 gap-3 mt-2">
      <div className="col-span-2"><Field label="Item"><Input className="rounded-sm" value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })} data-testid="pr-item" /></Field></div>
      <Field label="Quantity"><Input type="number" min={1} className="rounded-sm" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></Field>
      <Field label="Estimated cost ₹"><Input type="number" className="rounded-sm" value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })} data-testid="pr-cost" /></Field>
      <Field label="Vendor"><Input className="rounded-sm" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} /></Field>
      <Field label="Category">
        <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
          <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>{categories.map((c) => <SelectItem key={c.category} value={c.category}>{c.category}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <div className="col-span-2"><Field label="Justification"><Input className="rounded-sm" value={form.justification} onChange={(e) => setForm({ ...form, justification: e.target.value })} /></Field></div>
      {kind === "reimbursement" && (
        <div className="col-span-2">
          <Field label={<span className="inline-flex items-center gap-1"><LinkIcon size={11} /> Google Drive receipt link <span className="text-red-500">*</span></span>}>
            <Input className="rounded-sm" placeholder="https://drive.google.com/…"
                   value={form.receipt_url} onChange={(e) => setForm({ ...form, receipt_url: e.target.value })} data-testid="reimbursement-receipt" />
          </Field>
          <div className="text-[10px] text-slate-500 mt-1">Required — paste a link to the receipt/invoice proving this was already paid for out-of-pocket.</div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-overline">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
